import type { Role } from '@seller/shared';

/**
 * Thin fetch wrapper.
 *
 * The session lives in an httpOnly cookie, so there is no token to attach —
 * `credentials: same-origin` is the whole of the auth story on this side.
 *
 * Server errors arrive as `{ error, code? }`. The `code` is what distinguishes
 * "you need to finish signing in" from "you are not signed in", which the shell
 * uses to route rather than guessing from the status alone.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    /** The whole error body, for a refusal that carries what it needs answered. */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error?: string;
  code?: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  // Built up rather than spread with a conditional, because under
  // exactOptionalPropertyTypes an explicit `undefined` is not the same as an
  // absent property and RequestInit will not accept it. A FormData body
  // sets its own content type, boundary included; only JSON is labeled here.
  const requestInit: RequestInit = { credentials: 'same-origin', ...init };
  if (typeof init?.body === 'string') {
    requestInit.headers = { 'Content-Type': 'application/json', ...init.headers };
  }

  try {
    response = await fetch(path, requestInit);
  } catch {
    throw new ApiError('Cannot reach the server. Is the API running?', 0);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const errorBody = (body ?? {}) as ErrorBody;
    throw new ApiError(
      errorBody.error ?? `Request failed (${response.status})`,
      response.status,
      errorBody.code,
      errorBody as Record<string, unknown>,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** A multipart upload; the browser sets the content type. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
};

/** A query string from the values that are set, or empty. */
export function queryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

// ---------------------------------------------------------------------------
// Shapes returned by the API
// ---------------------------------------------------------------------------

export type { Role };

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  roleLabel: string;
  totpEnabled: boolean;
  mustChangePassword: boolean;
}

/** One row on the Users page. Dates arrive as ISO strings. */
export interface ManagedUser {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  isActive: boolean;
  totpEnabled: boolean;
  mustChangePassword: boolean;
  lockedUntil: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  /** The latest unused invitation, for an account not yet set up; null otherwise. */
  invite: { expiresAt: string; expired: boolean } | null;
}

/**
 * The answer to creating a user, resending an invitation, or sending a reset.
 *
 * `temporaryPassword` and `inviteUrl` are present only when mail is not
 * configured — the hand-off for a server without a mail server. When mail is
 * configured both are null and the message is emailed.
 */
export interface InviteResponse {
  user: ManagedUser;
  temporaryPassword?: string | null;
  inviteUrl: string | null;
  inviteExpiresAt: string;
}

// --- Reports and imports ----------------------------------------------------

export const REPORT_KINDS = [
  'all_listings',
  'all_orders',
  'ledger_detail',
  'business_by_child_asin',
  'sales_traffic_json',
  'inventory_health',
  'restock_recommendations',
  'fba_inventory',
] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/** One uploaded report file. Dates arrive as ISO strings; days as YYYY-MM-DD. */
export interface ReportFileView {
  id: number;
  kind: ReportKind;
  source: 'manual' | 'spapi';
  originalName: string | null;
  sha256: string;
  byteSize: number;
  periodStart: string | null;
  periodEnd: string | null;
  uploadedBy: string | null;
  uploadedByName: string | null;
  uploadedAt: string;
  parsedAt: string | null;
  rowCount: number | null;
  status: 'stored' | 'parsed' | 'failed';
  error: string | null;
  /** Neutral notices from the last successful parse (dropped optional columns, say). */
  warnings: string[];
  /**
   * All Listings only: SKUs the last parse marked no longer listed, and SKUs it
   * found listed again. Null for every other kind (and older listings files).
   */
  delistedCount: number | null;
  relistedCount: number | null;
  /**
   * All Listings only: SKUs the file leaves out that were not marked (a hand upload,
   * or a fetched file a guard stopped); the row offers the action to mark them.
   */
  absentCount: number | null;
  /**
   * All Listings only: a newer parsed listings file exists, so the absent count is
   * history and the "mark absent" action is not offered.
   */
  superseded: boolean;
}

export interface ImportOutcome {
  file: ReportFileView;
  counts: {
    inserted: number;
    updated: number;
    skipped: number;
    /** All Listings only: SKUs this full file marked no longer listed. */
    delisted?: number;
    /** All Listings only: no-longer-listed SKUs this file carries again. */
    relisted?: number;
    /** All Listings only: SKUs the file leaves out that were not marked. */
    absent?: number;
  };
  /** Neutral notices about the file (dropped optional columns, say). */
  warnings: string[];
}

/** One kind as the chooser sees it: whether it can be used, and why not. */
export interface ImportKindInfo {
  kind: ReportKind;
  label: string;
  enabled: boolean;
  disabledReason: string | null;
}

// --- Selling Partner API connection -----------------------------------------

export type SpapiFetchStatus = 'queued' | 'running' | 'done' | 'failed' | 'skipped';
export type SpapiFetchMode = 'full' | 'rolling' | 'backfill';

/**
 * A fetch's kind: a report kind, the competitor-offers poll, the product-catalog
 * fetch, or the FBA inbound-shipment fetch.
 */
export type SpapiFetchKind = ReportKind | 'offers' | 'catalog' | 'inbound';

/** One SP-API fetch, as the runs log and the per-kind rows show it. */
export interface SpapiFetchRun {
  id: number;
  kind: SpapiFetchKind;
  reportType: string;
  mode: SpapiFetchMode;
  trigger: 'scheduled' | 'manual';
  status: SpapiFetchStatus;
  windowFrom: string | null;
  windowTo: string | null;
  reportId: string | null;
  reportDocumentId: string | null;
  reportFileId: number | null;
  rowCount: number | null;
  inserted: number | null;
  updated: number | null;
  skipped: number | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  /** The stored file's name, when the fetch produced one. */
  fileName?: string | null;
}

/** One fetchable kind's report type and its most recent fetch. */
export interface SpapiKindStatus {
  kind: ReportKind;
  reportType: string;
  windowed: boolean;
  lastFetch: SpapiFetchRun | null;
}

/** The Amazon connection card's state. Never carries a credential. */
export interface SpapiStatus {
  configured: boolean;
  marketplaceId: string | null;
  marketplaceCountry: string | null;
  schedule: string;
  nextRunAt: string | null;
  lastTokenExchange: { ok: boolean; at: string } | null;
  running: ReportKind[];
  /** Kinds with queued backfill windows left by a restart, offered as "Resume". */
  resumableBackfills: { kind: ReportKind; queued: number }[];
  kinds: SpapiKindStatus[];
  /** The competitor-offers poll's state (its own section on the connection card). */
  offers: {
    lastPoll: SpapiFetchRun | null;
    polling: boolean;
    /** True when Amazon last refused the offers poll for the Pricing role (403). */
    pricingRoleMissing: boolean;
  };
  /** The product-catalog fetch's state (its own section on the connection card). */
  catalog: {
    lastFetch: SpapiFetchRun | null;
    fetching: boolean;
    /** True when Amazon last refused the Catalog Items API for the listing role (403). */
    listingRoleMissing: boolean;
    /**
     * Every distinct product type the catalog resolved, most common first, with
     * the category it maps to (null when none) and whether it is a generic
     * catch-all; a type with neither is unmapped (a listing issue).
     */
    productTypes: {
      productType: string;
      count: number;
      category: TaxonomyCategory | null;
      generic: boolean;
    }[];
  };
  /** The FBA inbound-shipment fetch's state (its own section on the connection card). */
  inbound: {
    lastFetch: SpapiFetchRun | null;
    fetching: boolean;
    /** True when Amazon last refused the Fulfillment Inbound API for the role (403). */
    fulfillmentRoleMissing: boolean;
  };
}

/** The result of the "Test connection" button: ok/failed only. */
export interface SpapiTestResult {
  ok: boolean;
  at: string;
  message?: string;
}

// --- Outgoing mail ---------------------------------------------------------

export type MailStatusValue = 'queued' | 'sent' | 'failed' | 'held';

/** One row of the outbox, as the Email page lists it. */
export interface MailOutboxView {
  id: string;
  kind: string;
  toEmail: string;
  toName: string | null;
  subject: string;
  status: MailStatusValue;
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string;
  sentAt: string | null;
  createdAt: string;
}

/** Whether mail is configured, the outbox counts, and the recent messages. */
export interface MailStatus {
  configured: boolean;
  provider: string | null;
  from: { address: string; name: string };
  counts: { queued: number; sent: number; failed: number; held: number };
  recent: MailOutboxView[];
}

export interface DateRange {
  start: string;
  end: string;
}

/** A suggested download; `more` means it was capped and continues past `end`. */
export interface SuggestedRange extends DateRange {
  more?: boolean;
}

/** What the data rows cover for one kind, and the next download worth fetching. */
export interface CoverageEntry {
  kind: ReportKind;
  from: string | null;
  through: string | null;
  gaps: DateRange[];
  missingMonths: string[];
  distinctDays: number | null;
  skuCount: number | null;
  nextSuggestedRange: SuggestedRange | null;
  note: string | null;
}

// --- SKUs -----------------------------------------------------------------

/** Money and other numeric columns are strings, as the database returns them. */
export interface Sku {
  id: number;
  sellerSku: string;
  asin: string | null;
  fnsku: string | null;
  parentAsin: string | null;
  familyOverride: string | null;
  title: string | null;
  status: string | null;
  fulfillmentChannel: string | null;
  price: string | null;
  quantity: number | null;
  openDate: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  notes: string | null;
  // Owner-entered purchase-planning fields.
  reorderExcluded: boolean;
  reorderNote: string | null;
  casePack: number | null;
  minOrderQty: number | null;
  leadTimeDaysOverride: number | null;
  unitCost: string | null;
}

// --- Product families -------------------------------------------------------

export type FamilyKeySource = 'override' | 'parent_asin' | 'asin' | 'seller_sku';

/** The family a single SKU belongs to (on restock rows and the SKU detail). */
export interface SkuFamily {
  key: string;
  name: string;
  keySource: FamilyKeySource;
}

/** One row on the Families page: a product rolled up over its child SKUs. */
export interface FamilyRow {
  key: string;
  name: string;
  keySource: FamilyKeySource;
  childCount: number;
  activeChildren: number;
  inStockChildren: number;
  stock: number | null;
  units30: number;
  units90: number;
  units365: number;
  unitsLifetime: number;
  lastSaleDate: string | null;
  sessionsSinceLastSale: number;
  estLostUnits: string;
  unitsPer30InStock: string | null;
  unitsPer30InStockChildren: number;
  score: string;
  scoreBasis: 'in_stock_velocity' | 'active_months' | 'mixed';
  stockoutDays365Max: number;
  stockoutDays365Mean: string;
  dataQuality: string[];
  statusCounts: { status: string; count: number }[];
  hasInStockBasis: boolean;
  hasActiveMonthsBasis: boolean;
  /** Lowercased child SKUs/ASINs/titles, for client-side search. */
  searchText: string;
}

/** One child SKU within a family, with its variant label and mix shares. */
// --- Product taxonomy -------------------------------------------------------

export type TaxonomyCategory = 'racquet' | 'string' | 'grip' | 'footwear' | 'bag' | 'other';
export type TaxonomySource = 'override' | 'booking' | 'heuristic' | 'catalog';
export type TaxonomyConfidence = 'exact' | 'likely' | 'weak' | 'none';
/** A string's canonical gauge, thickest first (see docs/products.md). */
export type StringGauge = '15' | '15L' | '16' | '16L' | '17' | '17L' | '18' | '19' | '20';
/** How a string is packaged for sale. */
export type StringPutUp = 'set' | 'reel' | 'mini_reel' | 'half_set';

/** A string's gauge and put-up, each with the layer that supplied it; all null otherwise. */
export interface StringTaxonomyFields {
  gauge: StringGauge | null;
  /** The metric diameter stated with the gauge ("1.30"); null when none was. */
  gaugeMm: string | null;
  gaugeSource: TaxonomySource | null;
  putUp: StringPutUp | null;
  /** The put-up length as written ("40 ft", "200 m"). */
  putUpLength: string | null;
  putUpSource: TaxonomySource | null;
}

/** One SKU's product taxonomy on the SKU page (`GET /api/skus/:id`'s `taxonomy`). */
export interface SkuProductTaxonomy extends StringTaxonomyFields {
  /** The product identity: `asin:<ASIN>`, or `sku:<id>` for an ASIN-less SKU. */
  productKey: string;
  category: TaxonomyCategory | null;
  series: string | null;
  model: string | null;
  color: string | null;
  sizeLabel: string | null;
  sizeCanonical: string | null;
  /** Null before the first recompute has written a row for the product. */
  source: TaxonomySource | null;
  confidence: TaxonomyConfidence;
  listingIssue: ListingIssue | null;
  /** The listing issue in plain words, the review card's own sentence; null without one. */
  listingIssueReason: string | null;
  /** The catalog item's product type; null without a fetched record. */
  catalogProductType: string | null;
  /** Whether the product needs review (the review queue's own test). */
  inReview: boolean;
  /** At least one of the product's SKUs is Active. */
  live: boolean;
  /** No SKU is Active: the review queue hides it unless `?include=retired`. */
  retired: boolean;
  /** The Products browser drill that opens this product (sentinels for null nodes). */
  drill: { category: string; series: string; model: string; asin: string };
}

/** The resolved taxonomy for a SKU, exposed on the family children and SKU list. */
export interface SkuTaxonomy {
  category: TaxonomyCategory | null;
  series: string | null;
  model: string | null;
  color: string | null;
  sizeLabel: string | null;
  sizeCanonical: string | null;
  source: TaxonomySource;
  confidence: TaxonomyConfidence;
}

export interface FamilyChild {
  id: number;
  sellerSku: string;
  asin: string | null;
  title: string | null;
  variantLabel: string;
  variantSortKey: number;
  status: string | null;
  quantity: number | null;
  units30: number;
  units90: number;
  units365: number;
  unitsLifetime: number;
  lastSaleDate: string | null;
  unitsPer30InStock: string | null;
  sellThroughLastStocked: string | null;
  score: string;
  scoreBasis: 'in_stock_velocity' | 'active_months';
  dataQuality: string[];
  share365: string;
  shareLifetime: string;
  shareStock: string;
  /** A zero-stock duplicate on a shared child ASIN (hidden by default). */
  duplicateHidden: boolean;
  /** The resolved product taxonomy for this SKU (phase 2 renders it); null before recompute. */
  taxonomy: SkuTaxonomy | null;
}

/** The size mix rolled up per child ASIN, for SKUs that share a child ASIN. */
export interface ChildAsinMix {
  asin: string;
  skuCount: number;
  units365: number;
  unitsLifetime: number;
  stock: number;
  share365: string;
  shareLifetime: string;
  shareStock: string;
}

export interface FamiliesResult {
  families: FamilyRow[];
  stock: StockSummary;
  derived: AnalysisStatus;
  generatedAt: string;
}

export interface FamilyDetailResult {
  family: FamilyRow;
  children: FamilyChild[];
  byChildAsin: ChildAsinMix[];
  /** The true count of zero-stock duplicate child SKUs (whether or not this
   * response dropped them), so the page can offer the show/hide control. */
  hiddenDuplicates: number;
  stock: StockSummary;
  derived: AnalysisStatus;
}

export interface SkuListRow {
  id: number;
  sellerSku: string;
  asin: string | null;
  title: string | null;
  /** The effective status: Amazon's, or DELISTED_STATUS once a full listings import left it out. */
  status: string | null;
  /** Amazon's own last-known status, whatever the effective one says. */
  listingStatus: string | null;
  /** The day a full listings import first left the SKU out; null while listed. */
  delistedOn: string | null;
  quantity: number | null;
  units30: number;
  units90: number;
  units365: number;
  lastSaleDate: string | null;
  sellThroughLastStocked: string | null;
  unitsPer30InStock: string | null;
  /** The resolved product taxonomy for this SKU (phase 2 renders it); null before recompute. */
  taxonomy: SkuTaxonomy | null;
}

// --- Duplicate product entries ----------------------------------------------

/** Why the plan shows or hides a duplicate SKU (from the shared hide rule). */
export type HideReason =
  'unique_asin' | 'override' | 'has_stock' | 'active_kept' | 'no_stocked_sibling' | 'superseded';

/** Which duplicate set a child ASIN falls in. */
export type DuplicateKind = 'multiple_active' | 'active_plus_inactive';

/** One SKU on a duplicated child ASIN. */
export interface DuplicateSku {
  id: number;
  sellerSku: string;
  status: string | null;
  onHand: number | null;
  inbound: number;
  reserved: number;
  units365: number;
  unitsLifetime: number;
  lastSaleDate: string | null;
  firstSeenDate: string;
  /** True when the purchase plan currently hides this SKU. */
  duplicateHidden: boolean;
  /** Why the plan shows or hides it. */
  hideReason: HideReason;
  /** History fully captured (first sale after import, last sale in both coverages). */
  historyCaptured: boolean;
  /** When not captured, a short reason for the "Check first" note; null otherwise. */
  historyReason: string | null;
  /** A superseded empty duplicate whose history is captured: safe to remove. */
  readyToRemove: boolean;
  /** Already removed on Amazon: the day a full listings import first left it out; null while listed. */
  delistedOn: string | null;
}

/** One child ASIN carrying a duplicate seller SKU (2+ Active, or Active + non-Active). */
export interface DuplicateAsin {
  asin: string;
  kind: DuplicateKind;
  activeSkuCount: number;
  skuCount: number;
  family: SkuFamily | null;
  productName: string;
  skus: DuplicateSku[];
}

export interface DuplicatesResult {
  groups: DuplicateAsin[];
  stock: StockSummary;
  derived: AnalysisStatus;
  /** Imported order and ledger coverage, for the history-captured column note. */
  coverage: { order: Coverage; ledger: Coverage };
  generatedAt: string;
}

// --- Purchase plan ----------------------------------------------------------

export type VelocitySource = 'own_in_stock' | 'own_monthly' | 'family_mix' | 'seasonal' | 'none';
export type Confidence = 'high' | 'medium' | 'low';
export type DemandBasis = 'recent' | 'seasonal';
export type BudgetStatus = 'funded' | 'partial' | 'cut' | 'held' | 'uncosted';

/** The owner's purchase-planning settings. */
export interface PlanningSettings {
  leadTimeDays: number;
  coverDays: number;
  safetyPct: number;
  useLostDemand: boolean;
  roundToCasePack: boolean;
  countReservedInPosition: boolean;
  demandBasis: DemandBasis;
  /** YYYY-MM-DD; required on the seasonal basis. */
  horizonDate: string | null;
  /** A money string; null means no budget. */
  budget: string | null;
  /** The supplier booking the plan is placed against; null means none. */
  activeBookingId: number | null;
}

export type BookingWindow = 'ship_1' | 'ship_2' | 'ship_3' | 'q2' | 'q3' | 'q4';

/** The active booking line a SKU is ordered against (null when no booking or unmatched). */
export interface BookingPlanInfo {
  lineId: number;
  itemCode: string;
  unitCost: string | null;
  /** Whether the plan's cost is the booking line's own price or a SKU-cost fallback. */
  costSource: 'booking' | 'sku_fallback';
  priceLabel: string | null;
  map: string | null;
  mapPackQty: number | null;
  orderMultiple: number | null;
  unit: string | null;
  packQty: number;
  window: BookingWindow;
  availability: 'available' | 'prebook';
  prebook: boolean;
  launchDate: string | null;
  matchState: 'confirmed' | 'proposed_exact' | 'proposed_likely';
  cannotArriveByHorizon: boolean;
  sheetName: string;
  rowNumber: number;
  quantityColumn: string;
  /** The racquet/demo pair lever, present only on a clean racquet+demo pair line. */
  pair: BookingPairLever | null;
}

/** The per-pair "order from racquet tab" preference and the demo key to set it on. */
export interface BookingPairLever {
  /** true = order the racquet tab's cell/cost; null = the demo when orderable. */
  preferRacquet: boolean | null;
  /** prefer set but the racquet is not orderable, so the demo cell is ordered instead. */
  preferRacquetFallback: boolean;
  demoItemCode: string;
  demoSizeLabel: string;
  demoDiscriminator: string;
}

/** An orderable booking line matched to no SKU: in the booking, but unsized. */
export interface UnmatchedBookingLine {
  lineId: number;
  itemCode: string;
  model: string;
  color: string | null;
  sizeLabel: string | null;
  category: string;
  window: BookingWindow;
  availability: 'available' | 'prebook';
  prebook: boolean;
  unitCost: string | null;
  priceLabel: string | null;
  map: string | null;
  matchState: 'unmatched' | 'no_listing';
  /** 'conflicting decisions' or 'ambiguous demo pairing'; null for a plain unmatched line. */
  reason: string | null;
}

/** A SKU's seasonal figures (null on the recent basis). */
export interface SeasonalFigures {
  windowFrom: string;
  windowTo: string;
  lastYearUnits: number;
  lastYearInStockDays: number;
  lastYearPer30: string | null;
  trendFactor: string | null;
  trendClamped: boolean;
  seasonalPer30: string | null;
  used: boolean;
  horizonDays: number;
}

/** The budget's verdict on one line (null when no budget is set). */
export interface BudgetLine {
  status: BudgetStatus;
  qty: number;
  cost: string | null;
  rank: number;
}

export interface BudgetSummary {
  budget: string;
  allocated: string;
  remaining: string;
  planCost: string;
  funded: number;
  partial: number;
  cut: number;
  held: number;
  heldUnits: number;
  uncosted: number;
  uncostedUnits: number;
  unfundedUnits: number;
}

/** One SKU's purchase-plan row. Numeric figures are strings, as from the database. */
export interface PlanRow {
  id: number;
  sellerSku: string;
  asin: string | null;
  title: string | null;
  status: string | null;
  /** No longer listed on Amazon since this day (excluded like reorderExcluded); null while listed. */
  delistedOn: string | null;
  family: SkuFamily | null;
  variantLabel: string;
  variantSortKey: number;
  stock: number | null;
  stockUnknown: boolean;
  inbound: number;
  reserved: number;
  position: number;
  velocitySource: VelocitySource;
  scoreBasis: 'in_stock_velocity' | 'active_months';
  dailyVelocity: string;
  velocityPer30: string;
  recentPer30: string;
  lostPerDay: string;
  lostPer30: string;
  lostCapped: boolean;
  demand: string;
  daysOfCoverNow: string | null;
  projectedStockoutDate: string | null;
  leadTimeDays: number;
  orderByDate: string | null;
  late: boolean;
  suggestedQty: number;
  finalQty: number;
  overrideQty: number | null;
  overrideNote: string | null;
  casePack: number | null;
  minOrderQty: number | null;
  leadTimeDaysOverride: number | null;
  /** The unit cost driving the plan (booking cost when in a booking, else the SKU's). */
  unitCost: string | null;
  /** The owner's own SKU unit cost, for the inline editor. */
  skuUnitCost: string | null;
  extendedCost: string | null;
  /** The active booking line this SKU is ordered against; null when none. */
  booking: BookingPlanInfo | null;
  /** A booking is active and this SKU is not in it (hidden from the plan by default). */
  notInBooking: boolean;
  /** Extended MAP for orderQty (per-pack aware); null without a booking MAP. */
  orderMap: string | null;
  /** orderQty × unitCost; the cost of what is ordered. */
  orderCost: string | null;
  /** orderMap − orderCost (order net profit); null when either is unknown. */
  orderProfit: string | null;
  reorderExcluded: boolean;
  reorderNote: string | null;
  units30: number;
  units365: number;
  dataQuality: string[];
  confidence: Confidence;
  reasons: string[];
  seasonal: SeasonalFigures | null;
  budget: BudgetLine | null;
  /** The final quantity, narrowed to what the budget funds when one is set. */
  orderQty: number;
  /** A zero-stock duplicate on a shared child ASIN (hidden by default). */
  duplicateHidden: boolean;
}

/** One family's rolled-up purchase-plan row. */
export interface FamilyPlanRow {
  key: string;
  name: string;
  childCount: number;
  childrenToOrder: number;
  suggestedQty: number;
  finalQty: number;
  extendedCost: string | null;
  costedUnits: number;
  uncostedUnits: number;
  earliestOrderByDate: string | null;
  late: boolean;
  worstConfidence: Confidence | null;
  units365: number;
  stock: number | null;
  orderQty: number;
  /** The part of `orderQty` on uncosted children (never allocated, kept at their final). */
  uncostedOrderUnits: number;
  budgetCost: string | null;
  budgetStatusCounts: Record<BudgetStatus, number>;
  /** Sum of the children's extended MAP for what is ordered; null with no booking MAP. */
  orderMap: string | null;
  /** Sum of the children's order cost; null when none costed. */
  orderCost: string | null;
  /** orderMap − orderCost across the product; null when either is unknown. */
  orderProfit: string | null;
  /** Funded children with no MAP, so the product's net profit is partial when > 0. */
  orderMapMissing: number;
}

/** The active booking on the plan (null when none). */
export interface ActiveBooking {
  id: number;
  name: string;
  bookingYear: number;
}

export interface PurchasePlanResult<T = PlanRow> {
  scope: 'sku' | 'family';
  /** The plan being viewed (null before any plan exists). */
  plan: PlanMeta | null;
  rows: T[];
  settings: PlanningSettings;
  /** The budget allocation; null when no budget is set. */
  budget: BudgetSummary | null;
  stock: StockSummary;
  derived: AnalysisStatus;
  /** How many rows are zero-stock duplicates (regardless of the hide flag). */
  hiddenCount: number;
  /** The active booking, when one is set. */
  activeBooking: ActiveBooking | null;
  /** How many master SKUs are left out because no orderable booking line matches them. */
  notInBookingCount: number;
  /** Orderable booking lines matched to no SKU (shown so the owner sees what is unsized). */
  unmatchedBookingLines: UnmatchedBookingLine[];
  /** The business-day today the plan was computed against (YYYY-MM-DD). */
  today: string;
  generatedAt: string;
}

export type StockSource = 'fba_inventory' | 'snapshot' | 'listing';

export interface StockSummary {
  /** The date the on-hand figures speak for; null for the listing fallback. */
  asOf: string | null;
  source: StockSource;
}

/** The status value that stands for SKUs with no status, on the wire and in the UI. */
export const NO_STATUS = '__none__';

export interface SkuListResult {
  skus: SkuListRow[];
  /** How many rows match the status and search filters. */
  total: number;
  /** True when `total` exceeded the server's safety cap and `skus` was clipped. */
  truncated: boolean;
  /**
   * One entry per distinct status the master holds (plus NO_STATUS when any SKU
   * has no status), each counted over the search-filtered set and ignoring the
   * status filter, so the dropdown shows every option with its count — a
   * selected status never vanishes, and a zero-match status reads `Active (0)`.
   */
  statusCounts: { status: string; count: number }[];
  /** Which source and date the on-hand quantities speak for. */
  stock: StockSummary;
  /** The derived tables' status. */
  derived: AnalysisStatus;
}

export interface MonthBucket {
  month: string;
  units: number;
  orders: number;
  revenue: string;
  sessions: number;
  trafficUnits: number;
  receipts: number;
  shipments: number;
  customerReturns: number;
  adjustments: number;
  whseTransfers: number;
}

export interface LedgerEventView {
  id: number;
  eventDate: string;
  eventTime: string | null;
  fnsku: string | null;
  asin: string | null;
  msku: string | null;
  eventType: string | null;
  referenceId: string | null;
  fulfillmentCenter: string | null;
  disposition: string | null;
  reason: string | null;
  quantity: number;
}

export interface SkuDetail {
  sku: Sku;
  history: {
    months: MonthBucket[];
    firstSale: string | null;
    lastSale: string | null;
    inStockNow: boolean;
    onHand: number | null;
    stockAsOf: string | null;
    stockSource: StockSource;
    lastReceiptDate: string | null;
  };
  sellThrough: SellThroughDetail;
  derived: AnalysisStatus;
  family: SkuFamily;
  /** The resolved taxonomy of the SKU's product (by ASIN, or by SKU when it has none). */
  taxonomy: SkuProductTaxonomy;
  ledger: LedgerEventView[];
  /** The current plan and this SKU's override in it (the plan the Planning section edits). */
  /** The day a full listings file was accepted as leaving this SKU out; null while listed. */
  delistedOn: string | null;
  currentPlan: {
    id: number;
    name: string;
    status: PurchasePlanStatus;
    overrideQty: number | null;
    overrideNote: string | null;
  } | null;
}

// --- Named purchase plans ---------------------------------------------------

export type PurchasePlanStatus = 'pending' | 'sent';

/** One plan in the selector / list. */
export interface PlanSummary {
  id: number;
  name: string;
  status: PurchasePlanStatus;
  notes: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string;
  sentAt: string | null;
  sentByName: string | null;
  activeBookingId: number | null;
  budget: string | null;
  demandBasis: DemandBasis;
  frozenOrderCost: string | null;
  frozenOrderMap: string | null;
  frozenNetProfit: string | null;
  frozenLines: number | null;
  frozenUnits: number | null;
  isCurrent: boolean;
}

export interface PlanListResult {
  plans: PlanSummary[];
  currentPlanId: number | null;
}

/** The plan the plan page is viewing (its header meta). */
export interface PlanMeta {
  id: number;
  name: string;
  status: PurchasePlanStatus;
  sentAt: string | null;
  notes: string | null;
  frozenOrderCost: string | null;
  frozenOrderMap: string | null;
  frozenNetProfit: string | null;
  frozenLines: number | null;
  frozenUnits: number | null;
}

// --- Restock ranking --------------------------------------------------------

export interface RestockRow {
  id: number;
  sellerSku: string;
  asin: string | null;
  title: string | null;
  status: string | null;
  /** The day a full listings import first left the SKU out; null while listed. */
  delistedOn: string | null;
  quantity: number | null;
  inStockNow: boolean;
  units30: number;
  units90: number;
  units365: number;
  unitsLifetime: number;
  activeMonths: number;
  avgUnitsActiveMonth: string;
  lastSaleDate: string | null;
  daysSinceLastSale: number | null;
  sessionsSinceLastSale: number;
  conversionInStock: string | null;
  estLostUnits: string;
  score: string;
  scoreBasis: 'in_stock_velocity' | 'active_months';
  sellThroughLastStocked: string | null;
  unitsPer30InStock: string | null;
  windowDays: number;
  daysInStock365: number;
  stockoutDays365: number;
  daysCovered365: number;
  lastInStockDay: string | null;
  dataQuality: string[];
  /** The product family this SKU belongs to; null if it could not be resolved. */
  family: SkuFamily | null;
}

// --- Sell-through detail (one SKU) -----------------------------------------

export interface SellThroughWindow {
  endDay: string | null;
  windowDays: number;
  daysInStock: number;
  unitsShipped: number;
  avgOnHand: string | null;
  sellThrough: string | null;
  unitsPer30InStock: string | null;
  shortHistory: boolean;
}

export interface StockoutEpisode {
  start: string;
  end: string | null;
  days: number;
  ongoing: boolean;
}

export interface SellThroughMonth {
  month: string;
  monthEndOnHand: number | null;
  daysInStock: number;
  unitsShipped: number;
  sellThrough90: string | null;
}

export interface SellThroughDetail {
  hasData: boolean;
  hasLedger: boolean;
  hasSellable: boolean;
  basis: 'in_stock_velocity' | 'active_months';
  current: SellThroughWindow;
  lastStocked: SellThroughWindow;
  monthly: SellThroughMonth[];
  stockouts: StockoutEpisode[];
  anchor: 'census' | 'forward' | null;
  anchorReserved: boolean;
  knownFrom: string | null;
  knownTo: string | null;
  firstDay: string | null;
  lastDay: string | null;
  negativeDays: number;
  ledgerGaps: boolean;
  salesBeforeLedger: boolean;
  daysInStock365: number;
  stockoutDays365: number;
  daysCovered365: number;
  flags: string[];
}

/** The derived daily tables' rebuild status; drives the analysis banner. */
export interface AnalysisStatus {
  sourceChangedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  ok: boolean | null;
  error: string | null;
  rowCount: number | null;
  ledgerThrough: string | null;
  ledgerBehind: boolean;
  stockEstimated: boolean;
  newestCensus: string | null;
  dirty: boolean;
  running: boolean;
}

// --- Charts -----------------------------------------------------------------

/** One aligned bucket of the charts series. Numeric columns are strings. */
export interface ChartSeriesBucket {
  start: string;
  end: string;
  unitsOrdered: number;
  unitsShipped: number;
  revenue: string;
  unitsReceived: number;
  unitsReturned: number;
  onHandEnd: number | null;
  onHandAsOf: string | null;
  onHandEndEstimated: boolean;
  onHandKnownSkus: number;
  onHandTotalSkus: number;
  partial: boolean;
  coveredFrom: string;
  coveredTo: string;
  onHandAvg: string | null;
  daysInStock: number;
  daysStockout: number;
  daysUnknown: number;
  sessions: number | null;
  trafficUnits: number | null;
  conversionPct: string | null;
  estLostUnits: string;
}

export interface ChartSeriesMeta {
  scope: 'account' | 'family' | 'sku' | 'asin' | 'series' | 'model';
  key: string | null;
  /** The parent drill keys of a series/model scope; null for the other scopes. */
  parent: ChartTaxonomyParent | null;
  grain: 'day' | 'week' | 'month';
  from: string;
  to: string;
  ledgerThrough: string | null;
  stockEstimated: boolean;
  trafficApproximated: boolean;
  trafficGrain: 'day' | 'week' | 'month' | 'custom' | null;
  bucketsWithoutTraffic: string[];
  /** SKU scope: OTHER SKUs sharing the ASIN when the traffic is the ASIN's shared figure. */
  trafficSharedWithSkus: number | null;
}

export interface ChartSeriesResult {
  buckets: ChartSeriesBucket[];
  meta: ChartSeriesMeta;
}

/** The Products drill keys above a series (its category) or a model (both). */
export interface ChartTaxonomyParent {
  category: string;
  series?: string;
}

/** One bucket of a batched units series (GET /api/charts/series/batch). */
export interface ChartUnitsBucket {
  start: string;
  end: string;
  /** True when the range clips the bucket (the current month). */
  partial: boolean;
  unitsOrdered: number;
}

export interface ChartUnitsSeries {
  key: string;
  /** SKUs the key names under its parent; 0 for a key that names nothing. */
  skuCount: number;
  buckets: ChartUnitsBucket[];
}

export interface ChartUnitsBatchResult {
  series: ChartUnitsSeries[];
  meta: {
    scope: 'series' | 'model';
    parent: ChartTaxonomyParent;
    grain: 'day' | 'week' | 'month';
    from: string;
    to: string;
  };
}

export interface ChartTopRow {
  key: string;
  label: string;
  id: number | null;
  units: number;
  revenue: string;
}

export interface ChartYoyRow {
  month: number;
  /** Null for a month not yet reached this year. */
  thisYear: string | null;
  lastYear: string;
  /** The current, still-incomplete month (month-to-date vs a full year ago). */
  partial: boolean;
}

export interface ChartYoyResult {
  rows: ChartYoyRow[];
  /** The day "this year" is measured through. */
  asOf: string;
}

export interface ChartMixChild {
  sellerSku: string;
  label: string;
}

export interface ChartMixQuarter {
  quarter: string;
  total: number;
  units: Record<string, number>;
}

export interface ChartMixResult {
  children: ChartMixChild[];
  quarters: ChartMixQuarter[];
}

export interface ChartScatterPoint {
  id: number;
  sellerSku: string;
  title: string | null;
  sessions: number;
  units: number;
  conversionPct: string | null;
  /** True in stock, false out, null when stock is unknown for the SKU. */
  inStock: boolean | null;
  /** True when this SKU's traffic is only its ASIN's shared by-ASIN figure. */
  sharedAsin: boolean;
  /** When shared, how many OTHER SKUs share the ASIN; null otherwise. */
  sharedWithSkus: number | null;
}

export type ChartSegmentState = 'in' | 'out' | 'unknown';

export interface ChartTimelineSegment {
  start: string;
  end: string;
  state: ChartSegmentState;
}

export interface ChartStockoutRow {
  id: number;
  sellerSku: string;
  label: string;
  status: string | null;
  knownFrom: string | null;
  knownTo: string | null;
  segments: ChartTimelineSegment[];
}

export interface ChartStockoutResult {
  rows: ChartStockoutRow[];
  /** Scope SKUs with any day in range, for the "show more" control. */
  total: number;
}

// --- Competitor offers (Product Pricing API) --------------------------------

export type BuyBoxHolder = 'us' | 'other' | 'none' | 'unknown';

/** Whether we have an offer, honoring the top-20 window and whether we know our id. */
export type OurPresence = 'present' | 'not-in-top-20' | 'absent' | 'unknown';

/** One competing offer on a listing. Prices are strings, as from the database. */
export interface OfferListItem {
  sellerId: string | null;
  isOurs: boolean;
  channel: 'AFN' | 'MFN';
  listingPrice: string | null;
  shipping: string | null;
  landedPrice: string | null;
  isPrime: boolean;
  isBuyBoxWinner: boolean;
  feedbackRating: string | null;
  feedbackCount: number | null;
  shipsFromState: string | null;
  /** The owner's name for the seller id; null when unnamed, and always null on our own offer. */
  sellerName: string | null;
}

/** The latest snapshot's headline figures for one ASIN. */
export interface OfferLatest {
  capturedAt: string;
  ourSeller: boolean;
  weHaveBuyBox: boolean;
  buyBoxHolder: BuyBoxHolder;
  buyBoxSellerId: string | null;
  buyBoxSellerName: string | null;
  buyBoxPrice: string | null;
  buyBoxLanded: string | null;
  lowestPrice: string | null;
  lowestFbaPrice: string | null;
  lowestMfnPrice: string | null;
  offersTotal: number;
  offersFba: number | null;
  offersMfn: number | null;
  buyBoxEligibleOffers: number | null;
  ourPresence: OurPresence;
}

/** One seller's presence on a listing (first/last seen, still on it). */
export interface OfferSellerView {
  sellerId: string;
  /** The owner's name for the seller (`seller_alias`); null when unnamed, and on our own row. */
  name: string | null;
  isOurs: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  isFba: boolean;
  lastPrice: string | null;
  lastSeenBuyBoxAt: string | null;
  present: boolean;
}

export interface OfferHistoryPoint {
  capturedAt: string;
  offersTotal: number;
  lowestFbaPrice: string | null;
  weHaveBuyBox: boolean;
}

export interface OffersDetail {
  asin: string;
  /** The marketplace the seller profile links point at. */
  marketplaceId: string;
  latest: OfferLatest | null;
  offers: OfferListItem[];
  sellers: OfferSellerView[];
  history: OfferHistoryPoint[];
  competitorDisappeared: boolean;
  newCompetitor: boolean;
}

/** One ASIN's row in the account offers summary. */
export interface OfferSummaryRow {
  asin: string;
  capturedAt: string;
  sellerCount: number;
  offersFba: number | null;
  lowestPrice: string | null;
  lowestFbaPrice: string | null;
  ourPrice: string | null;
  ourSeller: boolean;
  buyBoxHolder: BuyBoxHolder;
  buyBoxSellerId: string | null;
  buyBoxSellerName: string | null;
  /** Who undercut us: the cheapest competing FBA offer priced below ours, and its name. */
  undercutSellerId: string | null;
  undercutSellerName: string | null;
  undercut: boolean;
  competitorDisappeared: boolean;
  newCompetitor: boolean;
}

export interface OffersSummaryResult {
  rows: OfferSummaryRow[];
  flagDays: number;
  generatedAt: string;
}

/** One competitor the owner has named (`GET /api/sellers`). */
export interface SellerAliasView {
  sellerId: string;
  name: string;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  /** How many of our listings the seller has been seen on. */
  listings: number;
  lastSeenAt: string | null;
}

export interface SellerAliasList {
  sellers: SellerAliasView[];
  marketplaceId: string;
}

// --- FNSKU label printing (GET /api/labels/lookup; see docs/labels.md) --------

/** Where the lookup found a product: the entry itself, or one of the barcode sources. */
export type LabelMatchSource = 'asin' | 'fnsku' | 'sku' | 'upc' | 'booking' | 'catalog';

/** The resolved taxonomy shown on a Labeling row so the product can be confirmed. */
export interface LabelTaxonomy {
  category: TaxonomyCategory | null;
  series: string | null;
  model: string | null;
  color: string | null;
  sizeLabel: string | null;
  gauge: StringGauge | null;
  /** A `numeric` string from the database, shown as written. */
  gaugeMm: string | null;
  putUp: StringPutUp | null;
  putUpLength: string | null;
}

/** One product to label: its live seller SKU and that SKU's FNSKU. */
export interface LabelProduct {
  /** `asin:<ASIN>`, or `sku:<id>` for a SKU with no ASIN. */
  key: string;
  asin: string | null;
  skuId: number;
  /** The live seller SKU the label speaks for. */
  sellerSku: string;
  /** Its effective listing status. */
  status: string | null;
  fnsku: string | null;
  /** False when the live SKU has no FNSKU on file; `note` says so. */
  printable: boolean;
  note: string | null;
  title: string | null;
  taxonomy: LabelTaxonomy | null;
  skuCount: number;
  liveReason: 'only_sku' | 'others_superseded' | 'ranked';
  /** Other seller SKUs still live on the product when the choice was a ranking. */
  otherSkus: { sellerSku: string; status: string | null; fnsku: string | null }[];
  matchedBy: LabelMatchSource;
}

export interface LabelLookupResult {
  query: string;
  kind: 'gtin' | 'asin' | 'fnsku' | 'sku' | null;
  /** One product, several to choose from, or none. */
  status: 'found' | 'choose' | 'not_found';
  products: LabelProduct[];
}

export interface Summary {
  skus: { total: number; active: number };
  orderLines: number;
  lastImport: {
    id: number;
    kind: ReportKind;
    status: 'stored' | 'parsed' | 'failed';
    uploadedAt: string;
    rowCount: number | null;
  } | null;
}

// --- Order bookings ---------------------------------------------------------

/** One uploaded supplier booking workbook. */
export interface BookingView {
  id: number;
  name: string;
  bookingYear: number;
  originalName: string | null;
  byteSize: number;
  sheetNames: string[];
  warnings: string[];
  uploadedByName: string | null;
  uploadedAt: string;
  lineCount: number;
}

export interface UploadBookingResult {
  booking: BookingView;
  lineCount: number;
  warnings: string[];
}

export type BookingMatchState =
  'confirmed' | 'no_listing' | 'proposed_exact' | 'proposed_likely' | 'proposed_weak' | 'unmatched';

/** The seller SKU tied to a booking line (decided or proposed). */
export interface BookingLinkedSku {
  id: number;
  sellerSku: string;
  asin: string | null;
  title: string | null;
}

/** One catalog row: a booking line with its effective match. Money is a string. */
export interface BookingCatalogRow {
  id: number;
  category: string;
  window: string;
  series: string | null;
  itemCode: string;
  model: string;
  color: string | null;
  packageType: string | null;
  sizeLabel: string | null;
  sizeCanonical: string | null;
  upc: string | null;
  matchDiscriminator: string;
  unit: string | null;
  packQty: number;
  orderMultiple: number | null;
  map: string | null;
  mapPackQty: number | null;
  wholesale: string | null;
  unitCost: string | null;
  priceLabel: string | null;
  availability: string;
  prebook: boolean;
  launchDate: string | null;
  remarks: string | null;
  superseded: boolean;
  alternate: boolean;
  /** Racquet only: demo twin orderable (true), present but not (false), or none (null). */
  demoTwinAvailable: boolean | null;
  sheetName: string;
  rowNumber: number;
  quantityColumn: string;
  matchState: BookingMatchState;
  matchVia: string | null;
  sku: BookingLinkedSku | null;
  /**
   * The racquet/demo pair "order from racquet tab" preference stored on this key
   * (true, false, or null when unset). Meaningful on a demo line in a clean pair.
   */
  preferRacquet: boolean | null;
  /** On a demo line: prefer set but no orderable racquet twin, so the demo is ordered. */
  preferRacquetFallback: boolean;
}

export interface BookingReviewCandidate {
  skuId: number;
  sellerSku: string;
  title: string | null;
  confidence: string;
  score: number;
}

export interface BookingReviewRow extends BookingCatalogRow {
  candidates: BookingReviewCandidate[];
}

export interface BookingReviewResult {
  rows: BookingReviewRow[];
  counts: {
    total: number;
    confirmed: number;
    noListing: number;
    proposed: number;
    unmatched: number;
    remaining: number;
  };
}

/** A match decision the review page sends. The server decides the method. */
export interface BookingMatchDecision {
  itemCode: string;
  sizeLabel: string | null;
  discriminator: string;
  skuId?: number | null;
  noListing?: boolean;
}

/** The result of re-matching a booking's undecided lines. */
export interface BookingRematchResult {
  updated: number;
}

// --- Product taxonomy review + dictionary -----------------------------------

export interface TaxonomyReviewSku {
  id: number;
  sellerSku: string;
  asin: string | null;
  title: string | null;
  status: string | null;
}

export interface TaxonomyReviewRow extends StringTaxonomyFields {
  asin: string | null;
  skuId: number | null;
  keyType: 'asin' | 'sku';
  skus: TaxonomyReviewSku[];
  titles: string[];
  category: TaxonomyCategory | null;
  series: string | null;
  model: string | null;
  color: string | null;
  sizeLabel: string | null;
  sizeCanonical: string | null;
  source: TaxonomySource | null;
  confidence: TaxonomyConfidence;
  suggestions: { category: TaxonomyCategory; series: string }[];
  /** Where Amazon's product type disagrees with the category, or maps to none. */
  listingIssue: ListingIssue | null;
  /** The one-line reason shown on the card ("Amazon lists this as SHOES …"). */
  listingIssueReason: string | null;
  /** The catalog item's product type; null without a fetched record. */
  catalogProductType: string | null;
  /** True when none of the product's SKUs is Active (listed only with `include=retired`). */
  retired: boolean;
}

/** A disagreement between Amazon's product type and the resolved category. */
export type ListingIssue = 'type_conflict' | 'type_unmapped';

export interface TaxonomyReviewResult {
  rows: TaxonomyReviewRow[];
  /** `remaining` is the rows returned; `listingIssues` how many of them carry an issue. */
  counts: { remaining: number; listingIssues: number };
  /**
   * The retired products (no Active SKU) in this slice of the queue: hidden by
   * default, listed with `include=retired` (`included`).
   */
  retired: { included: boolean; count: number; listingIssues: number };
}

export interface TaxonomySummary {
  total: number;
  byCategory: { category: string; count: number }[];
  bySource: { source: string; count: number }[];
  byConfidence: { confidence: string; count: number }[];
  /** What the review page works through by default (live products), listing issues included. */
  needsReview: number;
  /** Live products whose Amazon product type disagrees or is unmapped. */
  listingIssues: number;
  /** Retired products (no Active SKU) that would need review; hidden by default. */
  retiredNeedsReview: number;
  /** Retired products with a listing issue; hidden by default. */
  retiredListingIssues: number;
}

export interface TaxonomySeriesRow {
  id: number;
  category: TaxonomyCategory;
  series: string;
  aliases: string[];
}

/**
 * PUT /api/taxonomy: an override saved (with what was stored, after the server's
 * case-insensitive canonicalization) or cleared.
 */
/** Which rule chose a stored override value: a value in use, a standard respelling, or as typed. */
export type SavedRule = 'existing' | 'standard' | 'typed';

export interface TaxonomyOverrideResult {
  ok: boolean;
  cleared: boolean;
  saved?: {
    category: TaxonomyCategory | null;
    series: string | null;
    model: string | null;
    color: string | null;
    sizeLabel: string | null;
    gauge: StringGauge | null;
    putUp: StringPutUp | null;
    /** Per field sent, the rule that chose the stored value (see lib/saved-as.ts). */
    rules: Record<'series' | 'model' | 'color' | 'sizeLabel', SavedRule | null>;
  };
}

/** POST /api/taxonomy/series: the series, and whether it was new (false: one already existed, ignoring case). */
export interface TaxonomySeriesCreateResult {
  series: TaxonomySeriesRow;
  created: boolean;
}

/** The values the review card's pickers list for one category. */
export interface TaxonomyValues {
  category: TaxonomyCategory;
  /**
   * The dictionary's series for the category, then the series its products carry
   * that the dictionary lacks (most used first); one spelling per value ignoring case.
   */
  series: string[];
  /** The models, colors, and sizes products of the category carry now. */
  models: string[];
  colors: string[];
  sizes: string[];
  /** For a string: the canonical gauges and the put-ups; empty otherwise. */
  gauges: StringGauge[];
  putUps: StringPutUp[];
}

// --- Products browser -------------------------------------------------------

export type ProductLevel = 'category' | 'series' | 'model' | 'product';

/** One node at the category / series / model level of the Products browser. */
export interface ProductNode {
  level: ProductLevel;
  category: TaxonomyCategory | null;
  categoryKey: string;
  series: string | null;
  seriesKey: string | null;
  model: string | null;
  modelKey: string | null;
  label: string;
  childCount: number;
  productCount: number;
  skuCount: number;
  activeCount: number;
  inStockCount: number;
  onHand: number | null;
  units30: number;
  units90: number;
  units365: number;
  unitsLifetime: number;
  unitsPer30InStock: string | null;
  unitsPer30InStockChildren: number;
  sellThrough: string | null;
  stockoutDays365Max: number;
  stockoutDays365Mean: string;
  lastSaleDate: string | null;
  reviewNeeded: number;
  /** Child ASINs under the node — populated at the model level only, for the offers roll-up. */
  asins: string[];
}

export interface ProductNodesResult {
  level: 'category' | 'series' | 'model';
  nodes: ProductNode[];
  stock: StockSummary;
  derived: AnalysisStatus;
  generatedAt: string;
}

/** One seller SKU beneath a product (child ASIN), with the plan's hide verdict. */
export interface ProductSku {
  id: number;
  sellerSku: string;
  asin: string | null;
  title: string | null;
  status: string | null;
  onHand: number | null;
  inbound: number;
  reserved: number;
  units30: number;
  units90: number;
  units365: number;
  unitsLifetime: number;
  firstSaleDate: string | null;
  lastSaleDate: string | null;
  duplicateHidden: boolean;
  hideReason: HideReason;
  /** History fully captured (first sale after import, last sale in both coverages). */
  historyCaptured: boolean;
  /** When not captured, a short reason for the "Check first" note; null otherwise. */
  historyReason: string | null;
  /** The ranking's restock score, a numeric string. */
  score: string;
  /** Amazon's own last listing status (`status` is the effective one). */
  listingStatus: string | null;
  /** The day a full listings import first left the SKU out; null while listed. */
  delistedOn: string | null;
  firstSeenDate: string;
  lastSeenDate: string;
}

/** One product: a child ASIN, its SKUs summed, and the SKU list beneath it. */
export interface ProductRow extends StringTaxonomyFields {
  productKey: string;
  asin: string | null;
  skuId: number;
  name: string;
  category: TaxonomyCategory | null;
  series: string | null;
  model: string | null;
  color: string | null;
  sizeLabel: string | null;
  sizeSort: number | null;
  /** The product family the ASIN belongs to, for the embedded family-scope chart. */
  family: SkuFamily | null;
  skuCount: number;
  activeCount: number;
  onHand: number | null;
  inbound: number;
  units30: number;
  units90: number;
  units365: number;
  unitsLifetime: number;
  unitsPer30InStock: string | null;
  /**
   * The product's units per 30 days as one demand stream across its SKUs (never a
   * sum of per-SKU rates): what days of cover divides by.
   */
  velocityPer30: string;
  /** On hand (no inbound) ÷ daily velocity, one decimal; null when stock is unknown or velocity is 0. */
  daysOfCover: string | null;
  coverBand: CoverBand;
  lastSaleDate: string | null;
  reviewNeeded: boolean;
  skus: ProductSku[];
}

/** Imported coverage bounds, so the page can explain the history-captured column. */
export interface Coverage {
  from: string | null;
  to: string | null;
}

// --- The product page (GET /api/products/:asin, /api/products/sku/:skuId) ---

/** One seller SKU in a product's lineage. */
export interface LineageSku extends ProductSku {
  /** The SKU the product is sold under today. */
  live: boolean;
  /** The duplicates page's safe-to-remove reading (only on a duplicated ASIN). */
  readyToRemove: boolean;
  fnsku: string | null;
  notes: string | null;
}

/** The stored catalog attributes of a product's ASIN. */
export interface CatalogSummary {
  asin: string;
  title: string | null;
  brand: string | null;
  productType: string | null;
  variationTheme: string | null;
  parentAsin: string | null;
  color: string | null;
  size: string | null;
  style: string | null;
  hasImage: boolean;
  fetchedAt: string;
  lastStatus: string;
}

/** The lead-time detail for one product. */
export interface ProductLeadTimeDetail {
  stats: LeadTimeStats;
  bySku: SkuLeadTime[];
  openInboundUnits: number;
  lines: LeadTimeLine[];
}

/** Everything the product page shows, in one payload. */
export interface ProductPageResult {
  productKey: string;
  asin: string | null;
  product: Omit<ProductRow, 'skus'>;
  liveSkuId: number | null;
  /** The live SKU's restock score; null when no SKU is live. */
  score: string | null;
  duplicateKind: DuplicateKind | null;
  lineage: LineageSku[];
  taxonomy: SkuProductTaxonomy;
  history: SkuDetail['history'];
  sellThrough: { skuId: number; sellerSku: string; detail: SellThroughDetail }[];
  ledger: LedgerEventView[];
  plan: {
    /**
     * False when the plan was not computed in time, or failed: `rows` is then empty
     * and means "not known", never "no rows".
     */
    available: boolean;
    unavailableReason: 'timeout' | 'error' | null;
    current: { id: number; name: string; status: PurchasePlanStatus } | null;
    rows: PlanRow[];
  };
  leadTimes: ProductLeadTimeDetail;
  catalog: CatalogSummary | null;
  coverage: { order: Coverage; ledger: Coverage };
  stock: StockSummary;
  derived: AnalysisStatus;
  generatedAt: string;
}

export interface ProductsResult {
  level: 'product';
  products: ProductRow[];
  coverage: { order: Coverage; ledger: Coverage };
  stock: StockSummary;
  derived: AnalysisStatus;
  generatedAt: string;
}

export type CoverBand = 'unknown' | 'critical' | 'low' | 'healthy' | 'ample';
export type StockState = 'in' | 'out' | 'unknown';

export interface SizeAxisColumn {
  key: string;
  label: string;
  sort: number | null;
}

/** One carried product (child ASIN) inside a grid cell. */
export interface GridProduct {
  asin: string | null;
  productKey: string;
  skuId: number;
  skuCount: number;
  onHand: number | null;
  units90: number;
  velocityPerDay: string | null;
  daysOfCover: string | null;
  coverBand: CoverBand;
  stockState: StockState;
  /** A string's metric diameter ("1.30"); null otherwise. */
  gaugeMm: string | null;
  /** A string's put-up length as written ("40 ft"); null otherwise. */
  putUpLength: string | null;
}

/** A carried cell holds every product at that color and size (usually one). */
export interface GridCarried {
  kind: 'carried';
  columnKey: string;
  products: GridProduct[];
}

export interface GridNotCarried {
  kind: 'not_carried';
  columnKey: string;
  lineId: number;
  itemCode: string;
  /** The booking line's own category (e.g. `demo_racquet`), so the web can say which. */
  category: string;
  unitCost: string | null;
  map: string | null;
}

export type GridCell = GridCarried | GridNotCarried;

export interface GridRow {
  /** A string row's put-up; always null for a racquet or shoe (rows are colors only). */
  putUp: StringPutUp | null;
  color: string | null;
  cells: GridCell[];
}

/** One put-up's share of a string model, for the grid header. */
export interface PutUpMixEntry {
  putUp: StringPutUp | null;
  label: string;
  products: number;
  units365: number;
  /** Share of the model's 365-day units, one decimal, summing to 100.0. */
  share: string;
}

export interface SizeGridResult {
  category: TaxonomyCategory | null;
  series: string | null;
  model: string | null;
  columns: SizeAxisColumn[];
  rows: GridRow[];
  /** A string model's put-up mix; null for every other category. */
  putUpMix: PutUpMixEntry[] | null;
  stock: StockSummary;
  derived: AnalysisStatus;
}

// --- Sell-through scatter ---------------------------------------------------

export type ScatterMetric =
  | 'velocity30'
  | 'sellThrough'
  | 'daysOfCover'
  | 'onHand'
  | 'units90'
  | 'units365'
  | 'stockoutDays365'
  | 'sessions90'
  | 'conversion90';

export type ScatterColorBy = 'series' | 'category' | 'model' | 'stockState';

export type ScatterQuadrant = 'fast_thin' | 'fast_covered' | 'slow_thin' | 'slow_covered';

/** One plotted point in the sell-through scatter: a product (child ASIN) or a SKU. */
export interface ScatterPoint {
  key: string;
  label: string;
  asin: string | null;
  skuId: number;
  category: TaxonomyCategory | null;
  categoryKey: string;
  series: string | null;
  seriesKey: string | null;
  model: string | null;
  modelKey: string | null;
  colorGroup: string;
  stockState: StockState;
  /** The chosen x/y/size metric values (numeric strings); null when the point lacks one. */
  x: string | null;
  y: string | null;
  size: string | null;
  quadrant: ScatterQuadrant | null;
  velocity30: string | null;
  sellThrough: string | null;
  daysOfCover: string | null;
  onHand: number | null;
  units90: number;
  units365: number;
  stockoutDays365: number;
  sessions90: number;
  conversion90: string | null;
}

export interface ScatterUnplottable {
  key: string;
  label: string;
  reason: string;
}

export interface ProductScatterResult {
  level: 'product' | 'sku';
  metrics: { x: ScatterMetric; y: ScatterMetric; size: ScatterMetric; colorBy: ScatterColorBy };
  thresholds: { x: string; y: string };
  quadrants: { key: ScatterQuadrant; label: string }[];
  colorGroups: string[];
  points: ScatterPoint[];
  unplottable: ScatterUnplottable[];
  window: { from: string; to: string };
}

/** The body sent to PUT /api/taxonomy: a key (asin or skuId) plus fields or a clear. */
export interface TaxonomyOverrideBody {
  asin?: string | null;
  skuId?: number | null;
  clear?: boolean;
  category?: TaxonomyCategory | null;
  series?: string | null;
  model?: string | null;
  color?: string | null;
  sizeLabel?: string | null;
  sizeCanonical?: string | null;
  /** A string's gauge as typed ("16", "16L", "1.30 mm"); canonicalized by the server. */
  gauge?: string | null;
  /** A string's put-up as typed or picked ("Reel", "mini_reel"); canonicalized by the server. */
  putUp?: string | null;
  putUpLength?: string | null;
  /** The Amazon product type the card showed, accepted with the category (409 if it changed). */
  acknowledgedCatalogType?: string | null;
  /** False: this save does not accept Amazon's type; the earlier acknowledgment stands. */
  acknowledge?: boolean;
}

// --- Observed lead times ----------------------------------------------------

/** The rolled-up lead-time statistics for a group of matched observations. */
export interface LeadTimeStats {
  n: number;
  medianTotal: number | null;
  p80Total: number | null;
  latestTotal: number | null;
  medianSupplier: number | null;
  medianTransit: number | null;
  suggestedLeadTimeDays: number | null;
}

/** Per-seller-SKU lead-time statistics plus the open-inbound cross-check units. */
export interface SkuLeadTime extends LeadTimeStats {
  sellerSku: string;
  asin: string | null;
  openInboundUnits: number;
}

/** Per-model (category + series + model) lead-time statistics. */
export interface ModelLeadTime extends LeadTimeStats {
  category: string | null;
  series: string | null;
  model: string | null;
}

export type LeadTimeStatus = 'matched' | 'in_transit' | 'awaiting' | 'unmatched';

/** A shipment reference: the opaque id plus the human confirmation id (FBA…). */
export interface ShipmentRef {
  shipmentId: string;
  confirmationId: string | null;
}

/** One sent plan line resolved against a shipment and a ledger receipt. */
export interface LeadTimeLine {
  planId: number;
  planName: string;
  sentAt: string;
  skuId: number | null;
  sellerSku: string;
  asin: string | null;
  category: string | null;
  series: string | null;
  model: string | null;
  fundedQty: number;
  shipmentId: string | null;
  shipmentConfirmationId: string | null;
  shipmentStatus: string | null;
  shipmentCreatedAt: string | null;
  firstReceiptAt: string | null;
  /** Further shipments a split SKU was carried on (id + confirmation id); the interval uses the first. */
  otherShipments: ShipmentRef[];
  supplierDays: number | null;
  transitDays: number | null;
  totalDays: number | null;
  status: LeadTimeStatus;
}

/** Coverage counts for the purchase-plan panel: lines, distinct plans, matched shipments. */
export interface LeadTimeCounts {
  lines: number;
  plans: number;
  shipments: number;
  /** The earliest inbound plan created date fetched (YYYY-MM-DD), or null. */
  earliestPlanCreatedAt: string | null;
}

/** The overall + per-SKU aggregate for the purchase-plan page. */
export interface LeadTimeOverview {
  overall: LeadTimeStats;
  bySku: SkuLeadTime[];
  counts: LeadTimeCounts;
}

/** The per-SKU detail for the SKU detail Lead times tab. */
export interface SkuLeadTimeDetail {
  sellerSku: string;
  stats: LeadTimeStats;
  openInboundUnits: number;
  lines: LeadTimeLine[];
}

/** The overall and per-SKU observed lead times (purchase-plan page). */
export const fetchLeadTimes = (): Promise<LeadTimeOverview> =>
  api.get<LeadTimeOverview>('/api/lead-times');

/** Per-model observed lead times (Products browser). */
export const fetchLeadTimeModels = (): Promise<{ models: ModelLeadTime[] }> =>
  api.get<{ models: ModelLeadTime[] }>('/api/lead-times/models');

/** One SKU's observed lead-time lines and aggregate (SKU detail page). */
export const fetchSkuLeadTimes = (sellerSku: string): Promise<SkuLeadTimeDetail> =>
  api.get<SkuLeadTimeDetail>(`/api/lead-times/sku/${encodeURIComponent(sellerSku)}`);
